import React from "react";
import { Box, Typography } from "@mui/material";
import { IMAGE_URL } from "../../redux/Constant";

export const PURPLE = "rgb(109, 79, 160)";
export const BORDER = "#C5A7CF";

const STATUS_COLORS = {
  complete: { bg: "#e8f5e9", color: "#2e7d32", border: "#a5d6a7" },
  confirm: { bg: "#e8f5e9", color: "#2e7d32", border: "#a5d6a7" },
  pending: { bg: "#fff8e1", color: "#f57f17", border: "#ffe082" },
  processing: { bg: "#e3f2fd", color: "#1565c0", border: "#90caf9" },
  cancelled: { bg: "#ffebee", color: "#c62828", border: "#ef9a9a" },
};

export function StatusBadge({ label }) {
  const c = STATUS_COLORS[(label || "").toLowerCase()] || {
    bg: "#f5f5f5",
    color: "#616161",
    border: "#e0e0e0",
  };
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: 1.25,
        py: 0.25,
        borderRadius: "999px",
        fontSize: "11px",
        fontWeight: 700,
        textTransform: "capitalize",
        border: `1px solid ${c.border}`,
        background: c.bg,
        color: c.color,
      }}
    >
      {label || "—"}
    </Box>
  );
}

export function SectionLabel({ children }) {
  return (
    <Typography
      sx={{
        fontSize: "11px",
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: PURPLE,
        mb: 1,
        fontFamily: "ABLE",
      }}
    >
      {children}
    </Typography>
  );
}

export function parseAddress(raw) {
  try {
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function AddressLines({ addr }) {
  const f = (v) => (v ? String(v).trim() : "");
  const name = [f(addr.fname), f(addr.lname)].filter(Boolean).join(" ");
  const street = [addr.address_1, addr.address_2, addr.address_3, addr.address_4]
    .filter(f)
    .join(", ");
  const region = [addr.city, addr.state, addr.postal].filter(f).join(", ");

  return (
    <Box sx={{ fontSize: "14px", color: "#444", fontFamily: "ABLE", lineHeight: 1.7 }}>
      {name && <Typography sx={{ fontWeight: 600, color: "#000", fontFamily: "ABLE", fontSize: "14px" }}>{name}</Typography>}
      {f(addr.company) && <Typography sx={{ fontFamily: "ABLE", fontSize: "14px" }}>{addr.company}</Typography>}
      {street && <Typography sx={{ fontFamily: "ABLE", fontSize: "14px" }}>{street}</Typography>}
      {region && <Typography sx={{ fontFamily: "ABLE", fontSize: "14px" }}>{region}</Typography>}
      {f(addr.country) && <Typography sx={{ fontFamily: "ABLE", fontSize: "14px" }}>{addr.country}</Typography>}
      {f(addr.phone) && (
        <Typography sx={{ mt: 0.5, fontFamily: "ABLE", fontSize: "14px" }}>
          <span style={{ color: "#999" }}>Phone:</span> {addr.phone}
        </Typography>
      )}
      {f(addr.email) && (
        <Typography sx={{ fontFamily: "ABLE", fontSize: "14px" }}>
          <span style={{ color: "#999" }}>Email:</span> {addr.email}
        </Typography>
      )}
    </Box>
  );
}

const cellSx = { px: 2, py: 1.5, fontFamily: "ABLE", fontSize: "14px" };

// Order details, bill/ship addresses and the items table with totals.
// Shared by the view-order receipt and the payment page.
export function OrderSummary({ order }) {
  const billing = parseAddress(order.billing_address_info);
  const shipping = parseAddress(order.shipping_address_info);

  const orderDate = order.created
    ? new Date(order.created).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
    : "—";

  const hasDiscount = Number(order.discounted_amount) > 0;
  const hasShipping = Number(order.Shipping_fee) > 0;

  return (
    <>
      {/* Order Details + Addresses */}
      <Box className="pdf-avoid-break" sx={{ background: "#faf9fc", border: "1px solid #eee", borderRadius: "8px", p: 2.5 }}>
        <SectionLabel>Order Details</SectionLabel>
        <Typography sx={{ fontFamily: "ABLE", fontSize: "14px", color: "#444" }}>
          <strong>Order Number:</strong> BP{order.order_id}
        </Typography>
        <Typography sx={{ fontFamily: "ABLE", fontSize: "14px", color: "#444" }}>
          <strong>Order Date:</strong> {orderDate}
        </Typography>
        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
          <span>
            <span style={{ color: "#999", marginRight: "4px", fontSize: "13px" }}>Order Status:</span>
            <StatusBadge label={order.order_status} />
          </span>
          <span>
            <span style={{ color: "#999", marginRight: "4px", fontSize: "13px" }}>Payment:</span>
            <StatusBadge label={order.payment_status} />
          </span>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, border: "1px solid #eee", borderRadius: "8px", overflow: "hidden", mt: 2 }}>
          <Box sx={{ p: 2 }}>
            <Typography sx={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", color: "#999", mb: 1, fontFamily: "ABLE" }}>
              Bill to
            </Typography>
            <AddressLines addr={billing} />
          </Box>
          <Box sx={{ p: 2, borderTop: { xs: "1px solid #eee", sm: "none" }, borderLeft: { xs: "none", sm: "1px solid #eee" } }}>
            <Typography sx={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", color: "#999", mb: 1, fontFamily: "ABLE" }}>
              Ship to
            </Typography>
            <AddressLines addr={shipping} />
          </Box>
        </Box>
      </Box>

      {/* Items */}
      <Box>
        <SectionLabel>Items Ordered</SectionLabel>
        <Box sx={{ border: "1px solid #eee", borderRadius: "8px", overflow: "hidden" }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
            <Box component="thead" sx={{ background: "#faf9fc" }}>
              <Box component="tr">
                <Box component="th" sx={{ ...cellSx, textAlign: "left", color: "#666", fontWeight: 700 }}>Product</Box>
                <Box component="th" sx={{ ...cellSx, textAlign: "center", color: "#666", fontWeight: 700 }}>Qty</Box>
                <Box component="th" sx={{ ...cellSx, textAlign: "right", color: "#666", fontWeight: 700 }}>Unit Price</Box>
                <Box component="th" sx={{ ...cellSx, textAlign: "right", color: "#666", fontWeight: 700 }}>Total</Box>
              </Box>
            </Box>
            <Box component="tbody">
              {order.items.map((item, i) => {
                const lineTotal = Number(item.unit_price) * Number(item.quantity);
                return (
                  <Box component="tr" key={i} sx={{ borderTop: "1px solid #f0f0f0" }}>
                    <Box component="td" sx={cellSx}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                        {item.product_image && (
                          <Box
                            component="img"
                            src={`${IMAGE_URL}/${item.product_image}`}
                            alt={item.product_name}
                            sx={{ width: 44, height: 44, objectFit: "contain", borderRadius: "4px", border: "1px solid #eee", flexShrink: 0, background: "#fff" }}
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                        )}
                        <Typography sx={{ fontFamily: "ABLE", fontSize: "14px", fontWeight: 600, color: "#000" }}>
                          {item.product_name}
                        </Typography>
                      </Box>
                    </Box>
                    <Box component="td" sx={{ ...cellSx, textAlign: "center", color: "#555" }}>{item.quantity}</Box>
                    <Box component="td" sx={{ ...cellSx, textAlign: "right", color: "#555" }}>
                      ${Number(item.unit_price).toFixed(2)}
                    </Box>
                    <Box component="td" sx={{ ...cellSx, textAlign: "right", fontWeight: 700, color: "#000" }}>
                      ${lineTotal.toFixed(2)}
                    </Box>
                  </Box>
                );
              })}

              <Box component="tr" sx={{ background: "#faf9fc", borderTop: "1px solid #f0f0f0" }}>
                <Box component="td" colSpan={3} sx={{ ...cellSx, color: "#666", fontWeight: 600 }}>Product Total</Box>
                <Box component="td" sx={{ ...cellSx, textAlign: "right", fontWeight: 700, color: "#000" }}>
                  ${Number(order.product_total_price || 0).toFixed(2)}
                </Box>
              </Box>

              {hasShipping && (
                <Box component="tr" sx={{ borderTop: "1px solid #f0f0f0" }}>
                  <Box component="td" colSpan={3} sx={{ ...cellSx, color: "#666" }}>Shipping Fee</Box>
                  <Box component="td" sx={{ ...cellSx, textAlign: "right", fontWeight: 700, color: "#000" }}>
                    ${Number(order.Shipping_fee).toFixed(2)}
                  </Box>
                </Box>
              )}

              {hasDiscount && (
                <Box component="tr" sx={{ borderTop: "1px solid #f0f0f0", color: "#2e7d32" }}>
                  <Box component="td" colSpan={3} sx={{ ...cellSx, color: "#2e7d32" }}>
                    Discount{order.coupon_code ? ` (${order.coupon_code})` : ""}
                  </Box>
                  <Box component="td" sx={{ ...cellSx, textAlign: "right", fontWeight: 700, color: "#2e7d32" }}>
                    -${Number(order.discounted_amount).toFixed(2)}
                  </Box>
                </Box>
              )}

              <Box component="tr" sx={{ background: PURPLE }}>
                <Box component="td" colSpan={3} sx={{ ...cellSx, color: "#fff", fontWeight: 700 }}>Grand Total</Box>
                <Box component="td" sx={{ ...cellSx, textAlign: "right", color: "#fff", fontWeight: 700 }}>
                  ${Number(order.total_amount || 0).toFixed(2)}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}

export { cellSx };
